import { useMemo } from "react";
import buffer from "@turf/buffer";
import { SelectionFeature } from "src/js/stores/map/store";
import { useTrackedApplicationSelectionHandler } from "react-migration/domains/planning/utils/useTrackedApplicationSelectionHandler";
import { PlanningApplicationDetailSection } from "react-migration/domains/planning/components/PlanningApplicationDetail";
import { PlanningApplicationList } from "react-migration/domains/planning/components/PlanningApplicationList";
import { PlanningApplication } from "react-migration/domains/planning/types";
import { SubSectionHeader } from "react-migration/components/SubSectionHeader/SubSectionHeader";
import { SkeletonLoading } from "react-migration/components/SkeletonLoading";
import { useTranslation } from "react-migration/lib/i18n/useTranslation";
import { InfoPopover } from "react-migration/components/InfoPopover";
import { LayerTypeSelectionDetailViewProps } from "../../../types";
import { Truncated } from "../../../Bundle/Workbench/Truncated";
import { usePlanningApplicationsByGeometry } from "../SelectionSidebar/usePlanningApplicationsByGeometry";
import { PlanningApplicationsByAddress } from "../PlanningApplicationsByAddress";
import { usePlanningAppById } from "../usePlanningAppsById";
import { usePlanningApplicationListBySelection } from "../SelectionSidebar/usePlanningApplicationListBySelection";

/**
 * Hook responsible for fetching selected & related planning applications based
 * on an application id.
 *
 * TODO:
 * 1. Get all related applications (see `relatedApplications`) not just ones nearby.
 * 2. Get selected & related application in a single call ideally in Apollo Client
 */
function useRelatedApplications(planningApp?: PlanningApplication) {
  const bufferedGeometry = useMemo(() => {
    const geometry = planningApp?.boundary || planningApp?.location;
    return geometry && buffer(geometry, 100, { units: "meters" }).geometry;
  }, [planningApp?.boundary, planningApp?.location]);

  const { data: planningApplications, loading } = usePlanningApplicationsByGeometry(bufferedGeometry || null);

  const { relatedApplications, referencesToRelated } = useMemo(() => {
    const selectedPlanningApplication: PlanningApplication | undefined = planningApplications.find(
      (app) => app.id === planningApp?.id
    );

    const references = (selectedPlanningApplication?.references || []).map((refs) => refs.planningId);

    const isReferenced = selectedPlanningApplication?.isReferenced || [];

    return {
      relatedApplications: planningApplications.filter((app) => {
        return references.includes(app.id ?? "") || isReferenced.includes(app.id ?? "");
      }),
      referencesToRelated: selectedPlanningApplication?.references || [],
    };
  }, [planningApp?.id, planningApplications]);

  return { relatedApplications, referencesToRelated, loading } as const;
}

export function PlanningApplicationSelectionDetailView({
  selection,
  isSummary,
  setDetailSelection,
}: LayerTypeSelectionDetailViewProps) {
  const showOtherPossibleSelections = isSummary;
  const [planningApp, isLoadingSelectedApp] = usePlanningAppById(selection?.id);

  const { relatedApplications, referencesToRelated, loading: isLoadingRelated } = useRelatedApplications(planningApp);

  const selectedPlanningAppWithReferences = useMemo<PlanningApplication | null>(
    () => (planningApp ? { ...planningApp, references: referencesToRelated } : null),
    [planningApp, referencesToRelated]
  );

  if (isLoadingSelectedApp || !selectedPlanningAppWithReferences)
    return (
      <div className="relative bg-white border-8 border-white h-28" data-testid="ownership-selection-header-loading">
        <SkeletonLoading rows={2} showPadding={false} />
      </div>
    );

  return (
    <>
      <Truncated onSeeMoreInfo={() => setDetailSelection?.(selection)} isExpanded={!isSummary}>
        <PlanningApplicationDetailSection
          planningApplication={selectedPlanningAppWithReferences}
          setDetailSelection={setDetailSelection}
        />
        <div className="flex flex-col p-2 divide-y-2 divide-neutral-100 *:py-3 *:first:pt-0 *:last:pb-0">
          <RelatedApplicationsSection
            isLoading={isLoadingRelated}
            relatedApplications={relatedApplications}
            setDetailSelection={setDetailSelection}
          />
        </div>
      </Truncated>
      {showOtherPossibleSelections && (
        <OtherSelectedApplicationsSection selection={selection} setDetailSelection={setDetailSelection} />
      )}
    </>
  );
}

type RelatedApplicationsSectionProps = {
  isLoading: boolean;
  relatedApplications: PlanningApplication[] | null;
} & Pick<LayerTypeSelectionDetailViewProps, "setDetailSelection">;

const EMPTY_ARRAY: unknown[] = [];

function RelatedApplicationsSection({
  isLoading,
  relatedApplications,
  setDetailSelection,
}: RelatedApplicationsSectionProps) {
  const { t } = useTranslation();

  const selectApplicationHandler = useTrackedApplicationSelectionHandler(setDetailSelection, {
    cardName: "Planning application Detail View",
    clickDetail: "Related application",
  });

  return (
    <section className="flex flex-col gap-y-2">
      <SubSectionHeader count={isLoading ? undefined : relatedApplications?.length}>
        {t("planning.planning_application_list.planning_application_list.related_applications")}
      </SubSectionHeader>
      {isLoading ? (
        <div className="relative h-[38px] bg-white">
          <SkeletonLoading rows={1} showPadding={false} />
        </div>
      ) : (
        !!relatedApplications?.length && (
          <div className="border divide-y rounded-sm border-border-action divide-border-base-divider">
            <PlanningApplicationList
              onApplicationClick={selectApplicationHandler}
              planningApps={relatedApplications || EMPTY_ARRAY}
            />
          </div>
        )
      )}
    </section>
  );
}

interface OtherSelectedApplicationsSectionProps {
  selection: SelectionFeature;
  setDetailSelection?: (selection: SelectionFeature) => void;
}

function OtherSelectedApplicationsSection({ selection, setDetailSelection }: OtherSelectedApplicationsSectionProps) {
  const { t } = useTranslation();
  const { filteredPlanningApplications, loading } = usePlanningApplicationListBySelection(selection, {
    filterSelectionFromList: true,
  });

  const selectApplicationHandler = useTrackedApplicationSelectionHandler(setDetailSelection, {
    cardName: "Planning application Detail View",
    clickDetail: "Other application",
  });

  if (!filteredPlanningApplications.length) return null;

  return (
    <section className="flex flex-col p-2 gap-y-2">
      <SubSectionHeader count={filteredPlanningApplications.length}>
        Other visible planning applications
        <span className="mx-1">
          <InfoPopover size="xs">
            {t("planning.planning_application_list.planning_application_detail_view.other_visible_tooltip")}
          </InfoPopover>
        </span>
      </SubSectionHeader>
      <div>
        {loading ? (
          <div className="relative h-12 p-2 bg-white">
            <SkeletonLoading rows={1} showPadding={false} />
          </div>
        ) : (
          <PlanningApplicationsByAddress
            planningApplications={filteredPlanningApplications}
            onApplicationClick={selectApplicationHandler}
          />
        )}
      </div>
    </section>
  );
}
